// The report editor, extracted from ReportEditorPage so it can be mounted
// somewhere other than a route.
//
// 🔴 THIS COMPONENT READS NO ROUTING HOOK AND RENDERS NO <Link>. That is the
// whole point of the extraction and it is pinned by a test that renders it with
// no Router at all — the same contract ListingDetailBody / SubmissionDetailBody
// carry. The route wrapper owns `useParams` and the back link; the report
// request drawer on the loan's Financial Reporting tab mounts this directly, so
// B3 never leaves the loan to read and edit the report.
//
// The terminal action is the host's, because the two hosts do different things
// with the finished report: the /reports route downloads it, and the loan tab
// files it into the loan's DMS. What the body keeps is the draft buffer — which
// is why the host gets `flushDraft` rather than being asked to reimplement it.

import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, RotateCcw } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { reportsApi, downloadBlob } from "@/api/reports";
import { relativeTime } from "@/components/ux/relativeTime";
import { reportEditorKeys } from "./reportEditorKeys";

export interface ReportEditorPrimaryActionArgs {
  /**
   * Persists any pending edit. A host action that reads the stored markdown
   * server-side — filing it, rendering it — MUST await this first, or it
   * silently ships the last-saved version and the reviewer's edits vanish
   * with no error anywhere.
   */
  flushDraft: () => Promise<void>;
  /** True while the body has nothing editable (a legacy row needing a
   *  re-generate) or another mutation is in flight. */
  disabled: boolean;
}

interface ReportEditorBodyProps {
  reportId: string;
  /**
   * Replaces the built-in "Finalize & Download". Omit it on the /reports route,
   * which wants the download.
   */
  renderPrimaryAction?: (args: ReportEditorPrimaryActionArgs) => ReactNode;
  /** Hides the report's own title block where the host chrome already shows it. */
  showTitle?: boolean;
}

export function ReportEditorBody({
  reportId,
  renderPrimaryAction,
  showTitle = true,
}: ReportEditorBodyProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const reportQuery = useQuery({
    queryKey: reportEditorKeys.detail(reportId),
    queryFn: () => reportsApi.get(reportId),
    enabled: Boolean(reportId),
  });

  const [draft, setDraft] = useState<string>("");
  const [showPromptSource, setShowPromptSource] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);

  // Initialize the textarea once the report loads. Deliberately keyed off
  // reportId so switching report resets the buffer; in-place reloads (e.g.
  // after a Re-generate) re-seed via the explicit setDraft in that mutation.
  useEffect(() => {
    if (
      reportQuery.data?.editedMarkdown !== undefined &&
      reportQuery.data?.editedMarkdown !== null
    ) {
      setDraft(reportQuery.data.editedMarkdown);
    } else {
      setDraft("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, reportQuery.data?.editedMarkdown]);

  const saveDraftMutation = useMutation({
    mutationFn: (bodyText: string) => reportsApi.updateBody(reportId, bodyText),
    onSuccess: () => {
      toast({ tone: "success", message: "Draft saved." });
      void queryClient.invalidateQueries({ queryKey: reportEditorKeys.detail(reportId) });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const regenerateMutation = useMutation({
    mutationFn: () => reportsApi.regenerate(reportId),
    onSuccess: (dto) => {
      if (dto.editedMarkdown) setDraft(dto.editedMarkdown);
      toast({ tone: "success", message: "Re-generated." });
      void queryClient.invalidateQueries({ queryKey: reportEditorKeys.detail(reportId) });
      setConfirmRegenerate(false);
    },
    onError: (e: Error) => {
      toast({ tone: "error", message: e.message });
      setConfirmRegenerate(false);
    },
  });

  const finalizeMutation = useMutation({
    mutationFn: async () => {
      // Save any pending draft first so the rendered docx reflects the
      // textarea's current state without forcing a separate Save Draft.
      if (reportQuery.data && draft !== (reportQuery.data.editedMarkdown ?? "")) {
        await reportsApi.updateBody(reportId, draft);
      }
      return reportsApi.finalizeAndDownload(reportId);
    },
    onSuccess: (blob) => {
      const fileName = `${(reportQuery.data?.title ?? "report").replace(/[^a-zA-Z0-9-_]/g, "-")}.docx`;
      downloadBlob(blob, fileName);
      toast({ tone: "success", message: "Finalized — downloading." });
      void queryClient.invalidateQueries({ queryKey: reportEditorKeys.detail(reportId) });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const report = reportQuery.data;
  const hasEditableContent = useMemo(
    () => Boolean(report?.editedMarkdown ?? report?.originalModelOutput),
    [report],
  );
  const isEdited = useMemo(() => {
    if (!report) return false;
    return (report.editedMarkdown ?? "") !== (report.originalModelOutput ?? "");
  }, [report]);
  const lastEditedLabel = report?.lastEditedAt
    ? `Last edited ${relativeTime(report.lastEditedAt)}`
    : null;

  const flushDraft = async () => {
    if (!report) return;
    if (draft === (report.editedMarkdown ?? "")) return;
    await reportsApi.updateBody(reportId, draft);
    void queryClient.invalidateQueries({ queryKey: reportEditorKeys.detail(reportId) });
  };

  const busy =
    saveDraftMutation.isPending || regenerateMutation.isPending || finalizeMutation.isPending;

  if (reportQuery.isLoading) {
    return <p className="font-body text-body text-gray-mid">Loading report…</p>;
  }
  if (reportQuery.isError || !report) {
    return (
      <p className="font-body text-body text-status-error" role="alert">
        Could not load this report. Refresh or open it from the Reports list.
      </p>
    );
  }

  return (
    <div className="space-y-space-4">
      {showTitle && (
        <div>
          <h1 className="font-display text-h2 text-onyx">{report.title}</h1>
          <p className="font-body text-caption text-gray-mid">
            From template &apos;{report.promptTemplateName}&apos; v
            {report.promptTemplateVersion}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-space-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => saveDraftMutation.mutate(draft)}
          disabled={!hasEditableContent || saveDraftMutation.isPending}
        >
          {saveDraftMutation.isPending ? "Saving…" : "Save Draft"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setConfirmRegenerate(true)}
          disabled={regenerateMutation.isPending}
        >
          <RotateCcw size={14} aria-hidden /> Re-generate
        </Button>
        {renderPrimaryAction ? (
          renderPrimaryAction({ flushDraft, disabled: !hasEditableContent || busy })
        ) : (
          <Button
            type="button"
            onClick={() => finalizeMutation.mutate()}
            disabled={!hasEditableContent || finalizeMutation.isPending}
          >
            {finalizeMutation.isPending ? "Finalizing…" : "Finalize & Download"}
          </Button>
        )}
        <button
          type="button"
          onClick={() => setShowPromptSource(true)}
          aria-label="View prompt source"
          title="View prompt source"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-mid hover:text-onyx hover:bg-white-warm"
        >
          <Info size={16} aria-hidden />
        </button>
      </div>

      {hasEditableContent ? (
        <div className="space-y-space-2">
          <p className="font-body text-caption text-gray-mid">
            <span className="font-display font-semibold uppercase tracking-label">
              {isEdited ? "Edited" : "AI-generated"}
            </span>
            {lastEditedLabel ? <> · {lastEditedLabel}</> : null}
          </p>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck
            rows={28}
            className={[
              "w-full",
              "font-mono text-body text-onyx",
              "bg-white-pure border border-border-light rounded-md",
              "p-space-3",
              "focus:outline-none focus:ring-2 focus:ring-gold/50",
            ].join(" ")}
            aria-label="Report markdown editor"
          />
        </div>
      ) : (
        <div className="rounded-md border border-border-light bg-white-warm p-space-4">
          <p className="font-body text-body text-onyx">
            This report was generated before the editor was added. Click{" "}
            <strong>Re-generate</strong> to enable editing.
          </p>
          <p className="font-body text-caption text-gray-mid mt-space-2">
            Re-generating runs the original prompt through Claude again. Existing
            downloads stay available; the new content overwrites the editor buffer
            once generation completes.
          </p>
        </div>
      )}

      <Modal
        open={showPromptSource}
        onClose={() => setShowPromptSource(false)}
        title="Prompt source"
        description="The exact text sent to Claude, including any one-time edits."
        size="lg"
        footer={
          <Button type="button" variant="secondary" onClick={() => setShowPromptSource(false)}>
            Close
          </Button>
        }
      >
        <pre className="bg-white-warm rounded-md border border-border-light p-space-3 font-mono text-caption text-onyx whitespace-pre-wrap max-h-[60vh] overflow-y-auto">
          {/* v3.3 §5: render the verbatim outbound payload (post-edit) when
              available; legacy rows fall back to the older assembled-but-pre-edit
              snapshot. */}
          {report.renderedPrompt ?? report.bodyText}
        </pre>
      </Modal>

      <Modal
        open={confirmRegenerate}
        onClose={() => setConfirmRegenerate(false)}
        title="Re-generate report?"
        description="This will replace your edits with a fresh AI-generated version. Continue?"
        footer={
          <div className="flex gap-space-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirmRegenerate(false)}
              disabled={regenerateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isPending}
            >
              {regenerateMutation.isPending ? "Re-generating…" : "Re-generate"}
            </Button>
          </div>
        }
      >
        <p className="font-body text-body text-onyx">
          Existing edits to this report will be replaced. The original prompt and
          source data are re-resolved, so if anything has changed since the last
          generation, the new content reflects today&apos;s state.
        </p>
      </Modal>
    </div>
  );
}
